import { ChatConversationPanel } from '@/components/chat/conversation-panel';
import type { ChatRoomView } from '@matter_extractor/shared';
import { useParams } from '@tanstack/react-router';

interface ChatConversationProps {
  room: ChatRoomView;
}

/**
 * The conversation, S3 and S4 (chat spec §3, §7, §8).
 *
 * **An ADAPTER, and nothing else.** Everything the screen does lives in
 * `conversation-panel.tsx`; what this file owns is one problem: the panel needs
 * the LINK TOKEN - it addresses the history, the POST and the socket - and the
 * room view deliberately carries no token (§5), so there is nothing in `room`
 * to take it from.
 *
 * It is read from the router here rather than passed down, because the shell
 * (`routes/chat.$token.tsx`) hands the phases `room` alone and belongs to
 * another lot landing in parallel. The repo's own convention is that a route
 * reads its params and passes them down, so this is the seam and not the
 * destination: **when the shell passes `token`, this file collapses into the
 * panel's own props** and the panel stays untouched. Every other lot that needs
 * the token - the lobby's consent, the closure - has the same problem, so the
 * shell is where it gets solved once.
 *
 * `strict: false` because a component must not name a route: it makes the hook
 * answer whatever the mounted route has rather than assert a path, which is
 * also what lets the panel be tested without a router at all.
 */
export function ChatConversation({ room }: ChatConversationProps) {
  const params: { token?: string } = useParams({ strict: false });

  // The route cannot match without one, so this is defence and not a path. It
  // renders nothing rather than mounting a panel that would open a socket on
  // `/api/chat/undefined/ws`.
  if (params.token === undefined) return null;
  return <ChatConversationPanel room={room} token={params.token} />;
}
